import { useMemo } from "react";
import { useLocale } from "../../i18n/LocaleProvider";
import type { AgentStep } from "../../lib/chat";
import { operationDiff, parameterChanges, type DiffLine } from "../../lib/skillPatch";

function Line({ line }: { line: DiffLine }) {
  const range = line.highlight;
  return <div className={`skill-diff-line is-${line.kind}`}>
    <span className="skill-diff-sign" aria-hidden="true">{line.kind === "add" ? "+" : line.kind === "remove" ? "−" : " "}</span>
    <code>{range ? <>{line.text.slice(0, range[0])}<mark>{line.text.slice(...range)}</mark>{line.text.slice(range[1])}</> : line.text || " "}</code>
  </div>;
}

export function SkillPatchCard({ step }: { step: AgentStep }) {
  const { t } = useLocale();
  const patch = step.candidatePatch;
  const hunks = useMemo(() => patch?.operations.map(operationDiff) ?? [], [patch?.operations]);
  if (!patch) return null;
  const state = step.status === "running" ? "preparing" : step.status === "stopped" ? "stopped" :
    step.status === "error" || patch.success === false ? "failed" : patch.success === true ? "ready" : "unconfirmed";
  return (
    <details className={`skill-patch-card is-${state}`}>
      <summary>
        <span className="skill-patch-title">code.cs <span>· {t("skillPatch.count").replace("{count}", String(hunks.length))}</span></span>
        <span className="skill-patch-state">{t(`skillPatch.${state}`)}</span>
      </summary>
      <p className="skill-patch-hint">{t("skillPatch.readOnly")}</p>
      {state === "failed" && step.result?.output ? <p className="skill-patch-error" role="alert">{step.result.output}</p> : null}
      <div className="skill-diff-scroll" tabIndex={0} role="region" aria-label={t("skillPatch.diff")}>
        {hunks.map((lines, index) => (
          <div key={index} className={patch.failedIndex === index ? "skill-diff-hunk is-failed" : "skill-diff-hunk"}>
            <div className="skill-diff-heading">{t("skillPatch.operation").replace("{index}", String(index + 1))}{patch.failedIndex === index ? ` · ${t("skillPatch.failed")}` : ""}</div>
            {lines.map((line, i) => <Line key={i} line={line} />)}
          </div>
        ))}
      </div>
    </details>
  );
}

export function ParameterChanges({ baseline, proposed }: { baseline: unknown; proposed: unknown }) {
  const { t } = useLocale();
  const changes = parameterChanges(baseline, proposed);
  if (!changes.length) return null;
  return <section className="skill-parameter-changes" aria-label={t("skillPatch.parameters")}>
    <h4>{t("skillPatch.parameters")}</h4>
    {changes.map((change) => <details key={change.name}>
      <summary><span className={`skill-param-kind is-${change.kind}`}>{t(`skillPatch.${change.kind}`)}</span> {change.label} <code>{change.name}</code></summary>
      <div className="skill-param-values"><table>
        <thead><tr><th>{t("skillPatch.field")}</th><th>{t("skillPatch.before")}</th><th>{t("skillPatch.after")}</th></tr></thead>
        <tbody>{[...new Set([...Object.keys(change.before ?? {}), ...Object.keys(change.after ?? {})])].filter((field) => JSON.stringify(change.before?.[field]) !== JSON.stringify(change.after?.[field])).map((field) => {
          const key = `skillPatch.field.${field}`;
          return <tr key={field}><th>{t(key) === key ? field : t(key)}</th><td>{displayValue(change.before?.[field])}</td><td>{displayValue(change.after?.[field])}</td></tr>;
        })}</tbody>
      </table></div>
    </details>)}
  </section>;
}

function displayValue(value: unknown): string {
  return value === undefined ? "—" : typeof value === "string" && value.length > 0 ? value : JSON.stringify(value);
}
